import React, { useState } from "react";
import PropTypes from "prop-types";

import GenericCard from "components/UI/GenericCard/GenericCard";

import { Segmented } from "antd";
import { PercentageOutlined } from "@ant-design/icons";
import { toFixed } from "utils/helpers";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  Brush,
} from "recharts";

const BacktestPerformance = (props) => {
  const { backtestResults } = props;
  const [groupBy, setGroupBy] = useState("monthlyPnl");
  const [type, setType] = useState("percent");

  const chartData = backtestResults.metrics[groupBy].data.map((item) => ({
    ...item,
    positiveValue: item[type] >= 0 ? item[type] : 0,
    negativeValue: item[type] < 0 ? item[type] : 0,
  }));

  const averageValue = backtestResults.metrics[groupBy].average;

  const formatNumber = (value, type) => {
    if (type === "percent") {
      return `${value}%`;
    }
    return `$${value.toLocaleString()}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const value = data[type];
      const isPositive = value >= 0;

      return (
        <div className="p-3 border border-gray-600 rounded-lg bg-[#111827] shadow-lg">
          <div className="font-semibold text-white mb-2">{label}</div>
          <div
            className={`font-medium ${
              isPositive ? "text-green-400" : "text-red-400"
            }`}
          >
            {formatNumber(value, type)}
          </div>
          {data.win !== undefined && (
            <div className="text-gray-300 text-sm mt-1">
              {data.win ? "Profitable" : "Loss"} period
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <GenericCard
      title="Profit by Timeframe"
      maxHeight={1200}
      isCardOpenByDefault={false}
      className="table-card [&>.content]:p-0 [&>.content]:overflow-hidden [&>.content]:h-full"
    >
      <div className="p-4 pb-0">
        <div className="flex items-center gap-3">
          <Segmented
            options={[
              { label: "Monthly Profit", value: "monthlyPnl" },
              { label: "Yearly Profit", value: "yearlyPnl" },
            ]}
            value={groupBy}
            onChange={setGroupBy}
          />
          <Segmented
            options={[
              {
                label: "Profit",
                icon: <PercentageOutlined />,
                value: "percent",
              },
              { label: "USD", value: "value" },
            ]}
            value={type}
            onChange={setType}
          />
        </div>
      </div>

      <ResponsiveContainer
        width="100%"
        height="100%"
        minHeight={450}
        maxHeight={500}
      >
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          barCategoryGap="0%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#374151"
            strokeOpacity={1}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
            width={70}
            tickFormatter={(value) =>
              type === "percent" ? `${value}%` : `$${value.toLocaleString()}`
            }
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Average line */}
          {type === "percent" && (
            <ReferenceLine
              y={averageValue}
              stroke="#ef4444"
              strokeDasharray="4 4"
              strokeWidth={2}
              label={{
                value: `Average: ${toFixed(averageValue)}%`,
                position: "insideBottomLeft",
                offset: 10,
                style: {
                  textBaseline: "bottom",
                  fill: "#ef4444",
                  fontSize: "12px",
                  fontWeight: "500",
                },
              }}
            />
          )}

          {/* Positive values bar */}
          <Bar
            dataKey="positiveValue"
            radius={[4, 4, 0, 0]}
            fill="#4ade80"
            isAnimationActive={true}
          >
            <LabelList
              dataKey={type}
              position="top"
              formatter={(value) => {
                if (value === 0) return "";
                return type === "percent"
                  ? `${value}%`
                  : `$${value.toLocaleString()}`;
              }}
              fill="#9ca3af"
              fontSize={11}
              offset={8}
            />
          </Bar>

          {/* Negative values bar */}
          <Bar
            dataKey="negativeValue"
            radius={[4, 4, 0, 0]}
            fill="#f87171"
            isAnimationActive={true}
          />

          <Brush
            dataKey="label"
            height={25}
            stroke="#8884d8"
            fill="#374151"
            tickFormatter={(value) => value}
          />
        </BarChart>
      </ResponsiveContainer>
    </GenericCard>
  );
};

BacktestPerformance.propTypes = { backtestResults: PropTypes.object };

export default BacktestPerformance;
